// Preis-Rollout im Grundartikel (S2): reine Ansichts-Logik — Filter, Sortierung, URL-Zustand,
// Grundtexte. Bewusst KEINE Preis-/Floor-Rechnung: Ziel, Floor, Ceiling, Urteil und Folge je Scope
// kommen fertig vom Backend (GET /pricing/rollout/family/:sku/). Hier wird nur ausgewaehlt,
// verglichen und Ziel − Ist als Anzeige-Abstand gebildet.
import type { CreateRolloutAck, FamilyPortal, FamilyRolloutInfo, FamilyStart, FamilyVariante, RolloutPlanAnzeige, ScopeWirkung } from '@/api/priceRollout'
import { a2GrundText, a2SperreText } from '@/lib/a2-sperre'

/** Portale mit Häkchen (Entscheid 18.10: alle vier, Amazon + eBay vorausgewählt). Kaufland wird nicht geerntet. */
export const SICHT_PORTALE = ['amazon', 'ebay', 'shopify', 'metro'] as const
export type SichtPortal = typeof SICHT_PORTALE[number]

export type StatusFilter = 'aktiv' | 'nicht_aktiv' | 'alle'
/** Rollout-Stand (Plan 2026-10-30): `ausgerollt` = jedes angehakte Portal mit aktivem Angebot am Ziel, laufend oder gehalten. */
export type RolloutFilter = 'alle' | 'offen' | 'ausgerollt'
export type StrategieFilter = 'alle' | 'margin' | 'break_even' | 'uneinheitlich'
export type Sortierung = 'angebot' | 'variante' | 'strategie' | 'abstand'

export interface RolloutAnsicht {
  status: StatusFilter
  portale: SichtPortal[]
  strategie: StrategieFilter
  rollout: RolloutFilter
  sort: Sortierung
}

export const DEFAULT_ANSICHT: RolloutAnsicht = { status: 'aktiv', portale: ['amazon', 'ebay'], strategie: 'alle', rollout: 'alle', sort: 'angebot' }

const STATUS_WERTE: readonly StatusFilter[] = ['aktiv', 'nicht_aktiv', 'alle']
const STRATEGIE_WERTE: readonly StrategieFilter[] = ['alle', 'margin', 'break_even', 'uneinheitlich']
const SORT_WERTE: readonly Sortierung[] = ['angebot', 'variante', 'strategie', 'abstand']
const ROLLOUT_WERTE: readonly RolloutFilter[] = ['alle', 'offen', 'ausgerollt']

/** URL-Parameter des Blocks — Präfix `ro_`, damit andere Parameter der Seite (z. B. `tab`) unberührt bleiben. */
export const URL_PARAM = { status: 'ro_status', portale: 'ro_portale', strategie: 'ro_strategie', rollout: 'ro_rollout', sort: 'ro_sort' } as const
const KEINE = 'keine'

export const PORTAL_NAME: Record<FamilyPortal | 'base', string> = {
  amazon: 'Amazon', ebay: 'eBay', shopify: 'Shopify', metro: 'Metro', kaufland: 'Kaufland', base: 'Basis',
}
export const portalName = (p: string): string => PORTAL_NAME[p as FamilyPortal | 'base'] ?? p

/** Markenfarbe je Portal (Spaltenkopf, Gruppenkante, Kopf-Statistik). */
export const PORTAL_FARBE: Record<SichtPortal, string> = {
  amazon: '#A1660B', ebay: '#0288D1', shopify: '#10B981', metro: '#3061B0',
}

const wahl = <T extends string>(werte: readonly T[], v: string | null, fallback: T): T =>
  v !== null && (werte as readonly string[]).includes(v) ? (v as T) : fallback

export function ansichtAusUrl(p: URLSearchParams): RolloutAnsicht {
  const roh = p.get(URL_PARAM.portale)
  let portale = DEFAULT_ANSICHT.portale
  if (roh !== null) {
    const teile = roh === KEINE ? [] : roh.split(',')
    portale = SICHT_PORTALE.filter((s) => teile.includes(s))
  }
  return {
    status: wahl(STATUS_WERTE, p.get(URL_PARAM.status), DEFAULT_ANSICHT.status),
    portale,
    strategie: wahl(STRATEGIE_WERTE, p.get(URL_PARAM.strategie), DEFAULT_ANSICHT.strategie),
    rollout: wahl(ROLLOUT_WERTE, p.get(URL_PARAM.rollout), DEFAULT_ANSICHT.rollout),
    sort: wahl(SORT_WERTE, p.get(URL_PARAM.sort), DEFAULT_ANSICHT.sort),
  }
}

/** Neue Parameter-Kopie: Defaults fallen weg, fremde Parameter bleiben. */
export function ansichtInUrl(p: URLSearchParams, a: RolloutAnsicht): URLSearchParams {
  const n = new URLSearchParams(p)
  const setze = (key: string, wert: string, def: string) => (wert === def ? n.delete(key) : n.set(key, wert))
  setze(URL_PARAM.status, a.status, DEFAULT_ANSICHT.status)
  setze(URL_PARAM.strategie, a.strategie, DEFAULT_ANSICHT.strategie)
  setze(URL_PARAM.rollout, a.rollout, DEFAULT_ANSICHT.rollout)
  setze(URL_PARAM.sort, a.sort, DEFAULT_ANSICHT.sort)
  const geordnet = SICHT_PORTALE.filter((s) => a.portale.includes(s))
  setze(URL_PARAM.portale, geordnet.length === 0 ? KEINE : geordnet.join(','), DEFAULT_ANSICHT.portale.join(','))
  return n
}

/** „aktiv“ = mindestens ein angehaktes Portal mit echtem, frischem Angebot (Backend-Zustand `aktiv`). */
export function istAktiv(v: FamilyVariante, portale: readonly SichtPortal[]): boolean {
  return portale.some((p) => v.portale[p]?.angebot.zustand === 'aktiv')
}

export type StrategieKlasse = 'break_even' | 'margin' | 'uneinheitlich'
export function strategieKlasse(v: FamilyVariante): StrategieKlasse {
  return v.strategie.uneinheitlich ? 'uneinheitlich' : v.strategie.amazon
}

/**
 * Zielpreis, den ein Start an diesem Portal setzen würde — vom Backend ausgewählt, nicht gerechnet:
 * Amazon = Amazon-Spur; sonst der Scope des Basis-Starts für dieses Portal (Metro-Cent!), sonst die Basis-Spur.
 */
export function zielFuer(v: FamilyVariante, portal: FamilyPortal): number | null {
  if (portal === 'amazon') return v.spuren.amazon.ziel
  const sc = v.start.base.scopes.find((s) => s.portal === portal)
  return sc?.ziel ?? v.spuren.base.ziel
}

/** Anzeige-Abstand Ziel − Ist (gerundet auf Cent); null ohne Ist-Preis oder Ziel. */
export function abstand(v: FamilyVariante, portal: FamilyPortal): number | null {
  const ist = v.portale[portal]?.angebot.ist_preis ?? null
  const ziel = zielFuer(v, portal)
  if (ist === null || ziel === null) return null
  return Math.round((ziel - ist) * 100) / 100
}

/** Portale, deren echte Angebote als Link erscheinen — unabhängig vom Portalfilter. */
const LINK_PORTALE: readonly FamilyPortal[] = [...SICHT_PORTALE, 'kaufland']
/** Zustand je Angebot wie im Backend (`zeilenZustand`, `RANG`): aktiv und frisch > aktiv, aber länger nicht gesehen > offline > ausgeschlossen. */
type LinkZustand = 'aktiv' | 'unbestätigt' | 'offline' | 'ausgeschlossen'
const LINK_RANG: Record<LinkZustand, number> = { aktiv: 4, unbestätigt: 3, offline: 2, ausgeschlossen: 1 }
/** Wie Backend `AKTIV_FRISCH_STUNDEN`. */
const AKTIV_FRISCH_MS = 30 * 3600 * 1000
function linkZustand(status: string, zuletzt: string | null, jetzt: Date): LinkZustand {
  if (status === 'inactive') return 'offline'
  if (status !== 'active') return 'ausgeschlossen'
  return zuletzt !== null && jetzt.getTime() - new Date(zuletzt).getTime() <= AKTIV_FRISCH_MS ? 'aktiv' : 'unbestätigt'
}
/** Nur echte http(s)-Adressen mit Host; alles andere (leer, relativ, javascript:, kaputt) ergibt keinen Link. */
function gueltigeUrl(roh: string | null): string | null {
  const url = (roh ?? '').trim()
  if (!/^https?:\/\//i.test(url)) return null
  try {
    const u = new URL(url)
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname !== '' ? url : null
  } catch {
    return null
  }
}
const fmtTag = (iso: string): string =>
  new Date(iso).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit' })

/** Kurzname je Verkäuferkonto (Schlüssel aus der Ernte `stock_portal_listings.account_key`). */
export { kontoKurz } from '@/lib/rollout-klartext'

/** Angebotsnummer aus der URL: eBay-ItemID (`/itm/<id>`), Amazon-ASIN (`/dp/<ASIN>`); sonst null. */
function angebotsNummer(portal: FamilyPortal, url: string): string | null {
  if (portal === 'ebay') return url.match(/\/itm\/(?:[^/?#]+\/)?(\d+)(?:[/?#]|$)/)?.[1] ?? null
  if (portal === 'amazon') return url.match(/\/dp\/([A-Z0-9]{10})(?:[/?#]|$)/i)?.[1]?.toUpperCase() ?? null
  return null
}

type AngebotEintrag = FamilyVariante['portale'][FamilyPortal]['angebot']['angebote'][number]
/** Parent-ASIN nur bei Amazon und nur, wenn sie wirklich eine andere ASIN ist. */
function parentVon(portal: FamilyPortal, a: AngebotEintrag): string | null {
  const p = a.parent_asin ?? null
  return portal === 'amazon' && p !== null && /^[A-Z0-9]{10}$/.test(p) ? p : null
}

export interface AngebotsLink {
  portal: FamilyPortal
  url: string
  /** eBay-ItemID bzw. Amazon-ASIN — gleiche Nummer = gleiches Angebot. */
  nummer: string | null
  /** Nur Amazon: Parent-ASIN der Variationsfamilie; null = unbekannt oder keine Variante. */
  parent: string | null
  /** Linktext ohne Pfeil; mehrere Links eines Portals werden nummeriert. */
  text: string
  nr: number | null
  /** Mindestens ein Konto mit Status `active`; sonst gedämpft. */
  aktiv: boolean
  konten: string[]
  titel: string
}

/**
 * Echte Angebote einer Variante als Links (URL vom Backend, Ernte `stock_portal_listings.url`):
 * je Portal und URL ein Link, doppelte URLs zusammengefasst (bester Zustand je Angebot gewinnt), aktive zuerst.
 * Leere oder nicht-http(s)-URLs ergeben keinen Link.
 */
const istOffen = (z: LinkZustand): boolean => z === 'aktiv' || z === 'unbestätigt'

export function angebotsLinks(v: FamilyVariante, jetzt: Date = new Date()): AngebotsLink[] {
  const out: AngebotsLink[] = []
  for (const portal of LINK_PORTALE) {
    const jeUrl = new Map<string, { konten: string[]; zustand: LinkZustand; zuletzt: string | null; parent: string | null }>()
    for (const a of v.portale[portal]?.angebot.angebote ?? []) {
      const url = gueltigeUrl(a.url)
      if (url === null) continue
      const zustand = linkZustand(a.status, a.zuletzt_gesehen, jetzt)
      const da = jeUrl.get(url)
      if (!da) {
        jeUrl.set(url, { konten: [a.konto], zustand, zuletzt: a.zuletzt_gesehen, parent: parentVon(portal, a) })
        continue
      }
      da.parent ??= parentVon(portal, a)
      if (!da.konten.includes(a.konto)) da.konten.push(a.konto)
      if (LINK_RANG[zustand] > LINK_RANG[da.zustand]) da.zustand = zustand
      if (a.zuletzt_gesehen && (!da.zuletzt || a.zuletzt_gesehen > da.zuletzt)) da.zuletzt = a.zuletzt_gesehen
    }
    const offen = istOffen
    const eintraege = [...jeUrl].sort(([, x], [, y]) => Number(offen(y.zustand)) - Number(offen(x.zustand)))
    eintraege.forEach(([url, e], i) => {
      const name = portalName(portal)
      out.push({
        portal, url, nummer: angebotsNummer(portal, url), parent: e.parent,
        text: eintraege.length > 1 ? `${name} ${i + 1}` : name,
        nr: eintraege.length > 1 ? i + 1 : null,
        aktiv: offen(e.zustand),
        konten: e.konten,
        titel: `${name} · Konto ${e.konten.join(', ')} · ${e.zustand}${e.zuletzt ? ` · zuletzt gesehen ${fmtTag(e.zuletzt)}` : ''} – öffnet in neuem Tab`,
      })
    })
  }
  return out
}

const cent = (x: number): number => Math.round(x * 100)

/**
 * Ein Start würde nichts ändern: jeder Scope steht cent-genau auf seinem Ziel, und laut Backend (`folge`)
 * entsteht nur ein neuer Rollout oder der bestehende bleibt im Dead-Band unverändert. `ziel_aendern` und
 * `reaktiviert` behalten den Knopf — dort korrigiert erst der Start das gespeicherte Ziel bzw. den Status.
 * Ist je Scope: `base` = Basis-Spur (DR-Basispreis), sonst das echte Angebot des Portals. Unbekannter Ist = nicht am Ziel.
 */
export function startAmZiel(v: FamilyVariante, knopf: 'amazon' | 'base'): boolean {
  const scopes = v.start[knopf].scopes
  if (scopes.length === 0) return false
  return scopes.every((sc) => {
    const ist = sc.portal === 'base' ? v.spuren.base.ist_preis : (v.portale[sc.portal as FamilyPortal]?.angebot.ist_preis ?? null)
    if (ist === null || sc.ziel === null || cent(ist) !== cent(sc.ziel)) return false
    return sc.folge === 'neu' || sc.folge === 'unveraendert_dead_band'
  })
}

/**
 * Rollout-Stand eines Portals (Plan 2026-10-30); null = kein aktives Angebot (zählt nicht).
 * `am_ziel` = Angebotspreis centgenau auf dem Ziel; `ausnahme` = Ist-Preis vom Nutzer gehalten;
 * `laeuft` = Rollout des Portals aktiv (bei DR-Weiterleitung ohne eigenen Rollout: der Basis-Rollout); sonst `offen`.
 */
export type RolloutStand = 'am_ziel' | 'laeuft' | 'ausnahme' | 'offen'
export const spurVon = (p: FamilyPortal): 'amazon' | 'base' => (p === 'amazon' ? 'amazon' : 'base')

export function rolloutStand(v: FamilyVariante, p: FamilyPortal): RolloutStand | null {
  const zeile = v.portale[p]
  if (zeile?.angebot.zustand !== 'aktiv') return null
  const ist = zeile.angebot.ist_preis
  const ziel = zielFuer(v, p)
  if (ist !== null && ziel !== null && cent(ist) === cent(ziel)) return 'am_ziel'
  if (v.spuren[spurVon(p)].ausnahme) return 'ausnahme'
  const basis = p !== 'amazon' && v.start.base.fanout_portal === p
    ? v.start.base.scopes.find((s) => s.portal === 'base')?.bestehend ?? null
    : null
  if (zeile.rollout?.status === 'active' || basis?.status === 'active') return 'laeuft'
  return 'offen'
}

/** Variante über die angehakten Portale: `offen`, sobald eines offen ist; null ohne aktives Angebot. */
export function rolloutKlasse(v: FamilyVariante, portale: readonly SichtPortal[]): 'offen' | 'ausgerollt' | null {
  const staende = portale.map((p) => rolloutStand(v, p)).filter((x): x is RolloutStand => x !== null)
  if (staende.length === 0) return null
  return staende.includes('offen') ? 'offen' : 'ausgerollt'
}

export interface PortalStatistik { portal: SichtPortal; aktiv: number; am_ziel: number; laeuft: number; ausnahme: number; offen: number }

/** Je Portal mit aktiven Angeboten: wie viele am Ziel, laufend, gehalten, offen (Kopf des Grundartikels). */
export function rolloutStatistik(varianten: readonly FamilyVariante[]): PortalStatistik[] {
  return SICHT_PORTALE.map((portal) => {
    const z: PortalStatistik = { portal, aktiv: 0, am_ziel: 0, laeuft: 0, ausnahme: 0, offen: 0 }
    for (const v of varianten) {
      const st = rolloutStand(v, portal)
      if (st === null) continue
      z.aktiv++
      z[st]++
    }
    return z
  }).filter((z) => z.aktiv > 0)
}

/** Ist-Preis der Spur kann gehalten werden: Ist und Ziel bekannt und verschieden (centgenau). */
export function ausnahmeMoeglich(v: FamilyVariante, spur: 'amazon' | 'base'): boolean {
  const s = v.spuren[spur]
  const ziel = s.ziel_endziel ?? s.ziel
  return s.ist_preis !== null && s.ist_preis > 0 && ziel !== null && cent(s.ist_preis) !== cent(ziel)
}

function groessterAbstand(v: FamilyVariante, portale: readonly SichtPortal[]): number | null {
  let best: number | null = null
  for (const p of portale) {
    const d = abstand(v, p)
    if (d !== null && (best === null || Math.abs(d) > best)) best = Math.abs(d)
  }
  return best
}

const SKU_VERGLEICH = new Intl.Collator('de', { numeric: true, sensitivity: 'base' })
export const vergleicheSku = (a: string, b: string): number => SKU_VERGLEICH.compare(a, b)
const STRATEGIE_RANG: Record<StrategieKlasse, number> = { break_even: 0, margin: 1, uneinheitlich: 2 }

/**
 * Strategie-Kanal aus dem Portalfilter: eBay, Shopify und Metro folgen der Basis. Amazon + Basis-Seite = beide,
 * nur Amazon = Amazon, sonst Basis. Ohne Portal: beide (wie bisher).
 */
export function strategieZiel(portale: readonly SichtPortal[]): 'amazon' | 'base' | 'both' {
  const amazon = portale.includes('amazon')
  const basis = portale.some((p) => p !== 'amazon')
  return amazon && !basis ? 'amazon' : basis && !amazon ? 'base' : 'both'
}

export interface AngebotsGruppe {
  /** Links des gemeinsamen Angebots (leer = Variante ohne offenes Angebot im Gruppen-Portal). */
  links: AngebotsLink[]
  varianten: FamilyVariante[]
  /**
   * Parent-ASINs, über die die Gruppe läuft (Amazon-Variationsfamilie; sortiert, ohne Dubletten). Leer = Gruppe
   * über dasselbe Angebot. Angebote ohne Parent der Gruppe bleiben in `links` (nur dort ist das Angebot gleich).
   */
  parents: string[]
}

/**
 * Gleiche Auktion zusammen: Gruppen-Portal ist eBay (wenn angehakt), sonst das erste angehakte Portal.
 * Schlüssel = Menge der offenen Angebote (Nummer, sonst URL); nur exakt gleiche Mengen bilden eine Gruppe.
 * Amazon: Angebote mit bekannter Parent-ASIN zählen als Familie (`P:<Parent>`), so stehen Varianten zusammen.
 * Reihenfolge der Gruppen und Varianten bleibt die der Eingabe.
 */
export function gruppiereNachAngebot(
  varianten: readonly FamilyVariante[], portale: readonly SichtPortal[], jetzt: Date = new Date(),
): AngebotsGruppe[] {
  const portal: FamilyPortal = portale.includes('ebay') ? 'ebay' : portale[0] ?? 'ebay'
  const gruppen: AngebotsGruppe[] = []
  const jeSchluessel = new Map<string, AngebotsGruppe>()
  for (const v of varianten) {
    const links = angebotsLinks(v, jetzt).filter((l) => l.portal === portal && l.aktiv)
    // Schlüssel aus den offenen Einzelangeboten je Konto (vor dem URL-Zusammenfassen): gleiche ASIN auf
    // zwei Amazon-Konten sind zwei Angebote, ein inaktives Konto zählt nicht mit.
    const offen = (v.portale[portal]?.angebot.angebote ?? [])
      .filter((a) => gueltigeUrl(a.url) !== null && istOffen(linkZustand(a.status, a.zuletzt_gesehen, jetzt)))
    const schluessel = [...new Set(offen.map((a) => {
      const parent = parentVon(portal, a)
      return `${portal}:${parent !== null ? `P:${parent}` : angebotsNummer(portal, gueltigeUrl(a.url)!) ?? gueltigeUrl(a.url)}:${a.konto}`
    }))].sort().join(' ')
    const da = schluessel === '' ? undefined : jeSchluessel.get(schluessel)
    if (da) { da.varianten.push(v); continue }
    const parents = [...new Set(offen.map((a) => parentVon(portal, a)).filter((p): p is string => p !== null))].sort()
    const g: AngebotsGruppe = { links: links.filter((l) => l.parent === null), varianten: [v], parents }
    gruppen.push(g)
    if (schluessel !== '') jeSchluessel.set(schluessel, g)
  }
  return gruppen
}

export function filterUndSortiere(varianten: readonly FamilyVariante[], a: RolloutAnsicht): FamilyVariante[] {
  const gefiltert = varianten.filter((v) => {
    if (a.status === 'aktiv' && !istAktiv(v, a.portale)) return false
    if (a.status === 'nicht_aktiv' && istAktiv(v, a.portale)) return false
    if (a.strategie !== 'alle' && strategieKlasse(v) !== a.strategie) return false
    if (a.rollout !== 'alle' && rolloutKlasse(v, a.portale) !== a.rollout) return false
    return true
  })
  const nachSku = (x: FamilyVariante, y: FamilyVariante) => vergleicheSku(x.sku, y.sku)
  if (a.sort === 'strategie') {
    return gefiltert.sort((x, y) => STRATEGIE_RANG[strategieKlasse(x)] - STRATEGIE_RANG[strategieKlasse(y)] || nachSku(x, y))
  }
  if (a.sort === 'abstand') {
    return gefiltert.sort((x, y) => {
      const dx = groessterAbstand(x, a.portale)
      const dy = groessterAbstand(y, a.portale)
      if (dx === null || dy === null) return (dx === null ? 1 : 0) - (dy === null ? 1 : 0) || nachSku(x, y)
      return dy - dx || nachSku(x, y)
    })
  }
  return gefiltert.sort(nachSku)
}

/** Wirkungen, bei denen der Tick einen Rollout überspringt — kurzer Grund für die Anzeige. */
export const WIRKT_NICHT: Partial<Record<ScopeWirkung, string>> = {
  keine_regel: 'keine Regel', regel_aus: 'Regel aus', gesperrt: 'LIVE gesperrt (Freigabe fehlt)',
}

/** Verständlicher Sperrgrund eines Startknopfs; null = startbar. Rohgrund bleibt als Tooltip sichtbar. */
export function grundText(s: FamilyStart): string | null {
  const g = s.grund
  if (s.startbar || g === null) return null
  if (g.startsWith('exkl_bis_A2')) return 'Plus Versand: erst A2'
  if (g === 'ausnahme') return 'Ist-Preis gehalten'
  const a2Grund = a2GrundText(g)
  if (a2Grund !== null) return a2Grund
  if (g === 'fanout_ohne_angebot') {
    return s.fanout_portal
      ? `DR leitet an ${portalName(s.fanout_portal)} weiter – dort kein aktives Angebot`
      : 'DR leitet an ein Portal ohne aktives Angebot weiter'
  }
  const still = WIRKT_NICHT[g as ScopeWirkung]
  if (still) {
    const sc = s.scopes.find((x) => x.wirkung === g)
    return `${sc ? `${portalName(sc.portal)}: ` : ''}${still} – Start wirkt nicht`
  }
  if (g === 'kein_angebot') return 'Kein aktives Amazon-Angebot'
  if (g === 'ziel_ungueltig') return 'Kein gültiger Zielpreis'
  const a2 = a2SperreText(g)
  if (a2 !== g) return a2
  if (g === 'amazon_multi_account') return 'Amazon auf mehreren Konten – Start nicht möglich'
  if (g.startsWith('multi_portal_conflict')) return 'Laut DR mehrere Nicht-Amazon-Portale aktiv'
  if (g.includes('set_ek_unknown')) return 'Set-EK unbekannt – Mindestpreis nicht bestimmbar'
  if (g.startsWith('ek_unknown')) return 'EK fehlt – Mindestpreis nicht bestimmbar'
  if (g.startsWith('kill-switch')) return 'Not-Aus aktiv'
  if (g.includes('Metro-Netto-Cent')) return 'Metro: Ziel nicht als Netto-Cent darstellbar'
  if (/ unter Floor /.test(g)) return 'Ziel unter Mindestpreis'
  if (/ ueber Ceiling /.test(g)) return 'Ziel über Höchstpreis'
  return g
}

/** Server-Grund eines PATCH/POST-Fehlers für die Anzeige (exkl → „Plus Versand: erst A2“). */
export function fehlerText(message: string): string {
  if (message.startsWith('ausnahme:')) return 'Ist-Preis gehalten – erst die Ausnahme aufheben'
  return message.startsWith('exkl_bis_A2') ? 'Plus Versand: erst A2' : a2GrundText(message) ?? message
}

/**
 * Grund eines laufenden/pausierten Rollouts (error_message) in Klartext — die Tick-Sicherheit
 * (Plan 2026-10-19 S1) schreibt hier, warum kein Schritt erfolgt. Unbekanntes bleibt roh sichtbar.
 */
export function rolloutGrundText(msg: string | null): string | null {
  if (msg === null || msg === '') return null
  if (msg.startsWith('ek_unknown')) return 'EK fehlt – kein Schritt, bis der EK gepflegt ist'
  if (msg.startsWith('set_ek_unknown')) return 'Set-EK unvollständig – kein Schritt'
  if (msg.startsWith('live_read_failed')) {
    const detail = msg.replace(/^live_read_failed:?\s*/, '')
    return `Live-Preis nicht lesbar${detail ? ` (${detail})` : ''} – nach 72 h pausiert`
  }
  // Plan 2026-10-20: eBay ohne eigenen DR-Preis folgt der Basis (kein eBay-Write).
  if (msg.startsWith('ebay_folgt_basis_done')) return 'Nur DR geändert, eBay-Übernahme nicht bestätigt – fertig mit der Basis'
  if (msg.startsWith('ebay_folgt_basis')) return `Nur DR geändert, eBay-Übernahme nicht bestätigt${msg.includes('Basis-Rollout starten') ? ' – Basis-Rollout starten' : ''}`
  // Plan 2026-10-21: eBay direkt (ReviseInventoryStatus) — Halte-/Sperrgründe.
  if (msg.startsWith('ebay_direct_hold')) return `eBay direkt angehalten${detail(msg, 'ebay_direct_hold')}`
  if (msg.startsWith('ebay_direct_needs_pair') || msg.includes('ebay_direct_needs_pair')) return 'eBay direkt braucht einen Basis-Rollout (beide Regeln aktiv)'
  if (msg.includes('ebay_multi_listing')) return 'eBay direkt: mehrere aktive Angebote – bitte klären'
  if (msg.startsWith('ebay_no_listing')) return 'eBay direkt: kein aktives Angebot gefunden'
  if (msg.startsWith('ebay_canary_blocked')) return 'eBay direkt: Artikel nicht freigegeben'
  if (msg.startsWith('ebay_write_budget')) return 'eBay direkt: Schreibbudget erreicht – nächster Lauf'
  if (msg.startsWith('coupled_bounds_mismatch')) return `Basispreis außerhalb der eBay-Grenzen – Basis und eBay pausiert${detail(msg, 'coupled_bounds_mismatch')}`
  if (msg.startsWith('coupled_mode_mismatch')) return 'Basis und eBay in verschiedenen Modi (LIVE / nur Protokoll) – Regeln angleichen'
  if (msg.startsWith('exkl_bis_A2')) return 'Plus Versand: erst A2'
  if (msg.startsWith('kill-switch')) return 'Not-Aus'
  return a2GrundText(msg) ?? msg
}

/** Klartext-Detail hinter dem Code-Präfix (`code: detail`) in Klammern; leer, wenn keins da ist. */
function detail(msg: string, code: string): string {
  const d = msg.slice(code.length).replace(/^:?\s*/, '')
  return d ? ` (${d})` : ''
}

type BasisRollout = { id: number; status: string; ziel: number; fehler: string | null; plan?: RolloutPlanAnzeige | null }
export interface KopplungsPaar { basis: BasisRollout; ebay: FamilyRolloutInfo }

/**
 * Basis und eBay laufen gekoppelt (gleicher Takt, gleicher Schritt — Plan 2026-10-19 S2.3) und
 * werden deshalb als EIN Rollout angezeigt, sobald beide Scopes bestehen.
 */
export function kopplungsPaar(v: FamilyVariante): KopplungsPaar | null {
  const basis = v.start.base.scopes.find((s) => s.portal === 'base')?.bestehend ?? null
  const ebay = v.portale.ebay.rollout
  return basis && ebay ? { basis, ebay } : null
}

/**
 * Zustand des Paars für eine Zeile: gemeinsamer Status (oder „gemischt“), welcher Rollout die
 * Pause bzw. das Fortsetzen auslöst (das Backend nimmt den Partner mit) und der erste Grund.
 */
export function paarZustand(p: { basis: { id: number; status: string; fehler: string | null }; ebay: { id: number; status: string; fehler: string | null } }): {
  status: string; pauseId: number | null; fortsetzenId: number | null; grund: string | null
} {
  const glieder = [{ name: 'Basis', ...p.basis }, { name: 'eBay', ...p.ebay }]
  const status = p.basis.status === p.ebay.status ? p.basis.status : 'gemischt'
  const pauseId = glieder.find((g) => g.status === 'active')?.id ?? null
  const fortsetzenId = glieder.find((g) => g.status === 'paused' || g.status === 'error')?.id ?? null
  const mitGrund = glieder.find((g) => g.fehler)
  const grund = mitGrund
    ? (status === 'gemischt' || p.basis.fehler !== p.ebay.fehler ? `${mitGrund.name}: ` : '') + rolloutGrundText(mitGrund.fehler)
    : null
  return { status, pauseId, fortsetzenId, grund }
}

/** Kurztext je Tick-Aktion für die Rückmeldung nach dem Start. */
const AKTION_TEXT: Record<string, string> = {
  not_due: 'noch nicht fällig', ek_unknown: 'EK fehlt', set_ek_unknown: 'Set-EK unvollständig',
  exkl_bis_A2: 'Plus Versand: erst A2', live_read_failed: 'Live-Preis nicht lesbar',
  coupled_wait: 'wartet auf den eBay-Schritt', coupled_mode_mismatch: 'Basis und eBay in verschiedenen Modi',
  live_read_failed_paused: 'Live-Preis 72 h nicht lesbar – pausiert', paused_foreign: 'Preis wurde außerhalb geändert',
  no_rule: 'keine Regel', disabled: 'Regel aus', write_error: 'Schreibfehler',
  infeasible_boundary: 'Ziel außerhalb der Preisgrenzen', input_sanity_failed: 'Eingabeprüfung fehlgeschlagen',
  ebay_folgt_basis: 'Nur DR geändert, eBay-Übernahme nicht bestätigt', ebay_folgt_basis_done: 'Nur DR geändert, eBay-Übernahme nicht bestätigt – fertig',
  ebay_synchron: 'eBay direkt steht schon auf dem Basispreis', ebay_direct_hold: 'eBay direkt angehalten',
  ebay_direkt_done: 'eBay direkt fertig (steht auf dem Basispreis)',
  ebay_no_listing: 'eBay direkt: kein aktives Angebot', ebay_canary_blocked: 'eBay direkt: Artikel nicht freigegeben',
  ebay_write_budget: 'eBay direkt: Schreibbudget erreicht', coupled_bounds_mismatch: 'Basispreis außerhalb der eBay-Grenzen',
}

/**
 * Rückmeldung nach „Rollout starten“: Anlegen und erster Schritt (S2.1) in einem Satz. `warnung`,
 * wenn der Schritt aus einem Grund ausblieb, den der Nutzer kennen muss.
 */
export function startMeldung(res: CreateRolloutAck): { art: 'ok' | 'warnung'; text: string } {
  if (res.debounced) return { art: 'ok', text: 'Änderung zu klein – Ziel bleibt, Rollout unverändert' }
  const basis = res.created ? 'Rollout angelegt' : 'Rollout aktualisiert'
  const fs = res.first_step
  if (!fs || fs.status === 'aus' || fs.status === 'nichts_neu' || fs.status === 'already_done') return { art: 'ok', text: basis }
  if (fs.status === 'kill') return { art: 'warnung', text: `${basis} – Not-Aus aktiv, kein Schritt` }
  if (fs.status === 'lease_busy') return { art: 'ok', text: `${basis} – ein Lauf ist gerade aktiv, Schritt folgt beim nächsten Lauf` }
  if (fs.status === 'fehler') return { art: 'warnung', text: `${basis} – erster Schritt fehlgeschlagen: ${fs.error}` }
  if (fs.status !== 'ran') return { art: 'ok', text: basis }
  const aktionen = fs.results.map((r) => r.action)
  if (aktionen.includes('stepped')) return { art: 'ok', text: `${basis} – erster Schritt geschrieben` }
  if (aktionen.includes('dry_run_preview')) return { art: 'ok', text: `${basis} – erster Schritt nur protokolliert, kein Preis geschrieben` }
  const erste = aktionen.find((a) => a !== 'coupled_wait') ?? aktionen[0]
  if (erste === undefined) return { art: 'ok', text: basis }
  return { art: 'warnung', text: `${basis} – kein erster Schritt: ${AKTION_TEXT[erste] ?? a2GrundText(erste) ?? erste}` }
}

/** Sammelknopf: „+ eBay“ nur, wenn der Basis-Start der angehakten Varianten wirklich eBay mitnimmt (fanout_portal). */
export function sammelKnopfText(angehakt: readonly FamilyVariante[]): string {
  const mitEbay = angehakt.filter((v) => v.start.base.fanout_portal === 'ebay').length
  if (mitEbay === 0) return `Basis für ${angehakt.length} starten`
  if (mitEbay === angehakt.length) return `Basis + eBay für ${angehakt.length} starten`
  return `Basis für ${angehakt.length} starten (eBay bei ${mitEbay})`
}
